import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import {
  HdCompositeChartInfoV1,
  HdCompositeSourceRefV1,
} from '../bodygraph/hd-composite-chart.models';
import { deriveProfile } from '../bodygraph/basic-properties/hd-chart-properties.util';
import { extractCycleSourceCore } from './hd-cycle-chart.utils';

export interface HdBuildCompositeChartInput {
  chart1Preset: HdBodygraphChartPreset;
  chart1Id: string;
  chart2Preset: HdBodygraphChartPreset;
  chart2Id: string;
}

/**
 * Ref van één composite-kant: cycle-ref-kern + type/profiel-snapshot. Type komt
 * uit de propertiesSummary van de bron (gestampt bij elke save); het profiel
 * valt zo nodig terug op een live afleiding uit de activaties.
 */
function compositeSourceRef(preset: HdBodygraphChartPreset, chartId: string): HdCompositeSourceRefV1 {
  const core = extractCycleSourceCore(preset.birthData);
  const derivedProfile = deriveProfile(preset.planetaryActivations ?? []);
  return {
    chartId,
    name: preset.name,
    ...core,
    hdType: preset.propertiesSummary?.type ?? null,
    hdProfile: preset.propertiesSummary?.profile
      ?? (derivedProfile ? `${derivedProfile.personalityLine}/${derivedProfile.designLine}` : null),
  };
}

/** Auto-naam van een composite: "Sophie & Steven". */
export function buildCompositeChartName(chart1Name: string, chart2Name: string): string {
  return `${chart1Name} & ${chart2Name}`;
}

/**
 * Bouwt het self-contained composite-document (spec composite charts §3):
 * top-level = snapshot van chart 1 (zoals de natal-snapshot bij cycles), het
 * `composite`-blok draagt beide bron-refs + de volledige chart van de partner.
 * propertiesSummary wordt bij save gestampt door HdChartLibraryService
 * (gecombineerd, zoals bij cycle charts).
 */
export function buildCompositeChartPreset(input: HdBuildCompositeChartInput): HdBodygraphChartPreset {
  const chart1 = input.chart1Preset;
  const chart2 = input.chart2Preset;
  const name = buildCompositeChartName(chart1.name, chart2.name);
  const composite: HdCompositeChartInfoV1 = {
    schemaVersion: 1,
    source: compositeSourceRef(chart1, input.chart1Id),
    partner: compositeSourceRef(chart2, input.chart2Id),
    partnerChart: {
      birthData: chart2.birthData,
      configuration: chart2.configuration,
      planetaryActivations: chart2.planetaryActivations,
      metadata: chart2.metadata,
    },
  };
  return {
    schemaVersion: 1,
    id: `composite:${input.chart1Id}:${input.chart2Id}`,
    name,
    tabTooltip: name,
    source: { provider: 'egitura-composite', sourceChartId: input.chart1Id, sourceFile: '' },
    birthData: chart1.birthData,
    configuration: chart1.configuration,
    planetaryActivations: chart1.planetaryActivations,
    metadata: chart1.metadata,
    composite,
  };
}
